import { CommentReply } from "@/types/commentReply";
import { AccountType, Controllers, User } from "@infoland/iprova-api";
import { AnchorType, CommentState, CommentType, DirectedAt, DocumentCommentListMode, DocumentVersionState, SystemUserIds } from "@/enums";

export interface Comment {
    comment_id: number;
    text: string;
    state: CommentState;
    type: CommentType;
    creator_id: string;
    creator_name: string;
    creator_email: string;
    creator_telephone: string;
    created_datetime: Date;
    comment_replies: CommentReply[];
    directed_at: DirectedAt;
    deleted?: boolean;
    anchor_text: string;
    anchor_id: string;
    anchor_type: AnchorType;
    document: DocumentMini;
}

export interface DocumentMini {
    document_id: string;
    title: string;
    version: number;
    revision: number;
}
export interface ApiComment {
    comment_id: number;
    text: string;
    state: CommentState;
    type: CommentType;
    creator_id: string;
    creator_name: string;
    created_datetime: string;
    comment_replies: CommentReply[];
    directed_at: DirectedAt;
    deleted?: boolean;
    anchor_text: string;
    anchor_id: string;
    anchor_type: AnchorType;
    document: DocumentMini;
}

export async function loadDocumentCommentsAsync(urlPath: string): Promise<Comment[]> {
    let apiComments: ApiComment[] = await Controllers.GenericController.getAsync<ApiComment[]>(urlPath);
    return apiComments.map(x => {
        return {
            comment_id: x.comment_id,
            text: x.text,
            state: x.state as string,
            type: x.type,
            creator_id: x.creator_id,
            creator_name: x.creator_name,
            created_datetime: x.created_datetime.toDate(),
            comment_replies: x.comment_replies,
            directed_at: x.directed_at,
            deleted: x.deleted,
            anchor_text: x.anchor_text,
            anchor_id: x.anchor_id,
            anchor_type: x.anchor_type,
            document: x.document
        };
    }) as Comment[];
}

export function filterCommentsByDocumentCommentListMode(
    comments: Comment[],
    documentVersionNumber: number,
    documentVersionState: DocumentVersionState,
    documentCommentListMode: DocumentCommentListMode,
    currentUser: User
): Comment[] {
    let commentsForReturn: Comment[] = null;
    if (!comments || comments.length === 0) {
        return [];
    }
    switch (documentVersionState) {
        case DocumentVersionState.Review:
        case DocumentVersionState.Draft:
        case DocumentVersionState.WaitingForPublication:
            commentsForReturn = comments.filter(
                c =>
                    (c.state && c.state !== CommentState.Fixed && c.state !== CommentState.Rejected) ||
                    (c.type === CommentType.Reader && c.document && c.document.version === documentVersionNumber - 1) ||
                    ((c.type === CommentType.Reviewer || c.type === CommentType.Writer) && c.document && c.document.version === documentVersionNumber)
            );
            break;
        case DocumentVersionState.Published:
        case DocumentVersionState.Expired:
            commentsForReturn = comments.filter(
                c =>
                    (c.state && c.state !== CommentState.Fixed && c.state !== CommentState.Rejected) ||
                    ((c.type === CommentType.Reader || c.type === CommentType.Writer) && c.document && c.document.version === documentVersionNumber)
            );
            break;
    }

    switch (documentCommentListMode) {
        case DocumentCommentListMode.review:
            commentsForReturn = commentsForReturn.filter(c => c.type !== CommentType.Writer || c.directed_at !== DirectedAt.Writers);
            break;
        case DocumentCommentListMode.read:
            commentsForReturn = commentsForReturn.filter(
                c =>
                    (c.type === CommentType.Reader &&
                        c.creator_id !== SystemUserIds.AnonymousUser &&
                        c.creator_id === currentUser.user_id &&
                        currentUser.account_type === AccountType.personal &&
                        c.state === CommentState.New) ||
                    (c.document && c.document.version === documentVersionNumber && c.state !== CommentState.New) ||
                    (c.document && c.document.version < documentVersionNumber && c.state === CommentState.Accepted)
            );
            commentsForReturn.forEach(c => {
                if (c.comment_replies) {
                    c.comment_replies = c.comment_replies.filter(cr => cr.comment_type !== CommentType.Reviewer);
                }
            });
            break;
    }

    return commentsForReturn;
}
